import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Gift, Users, Building2, Heart, ChevronDown, ChevronUp,
  Star, MapPin, Calendar, Shield, Award, Plane, Search,
  CheckCircle, ArrowRight, Sparkles, Home as HomeIcon, Utensils, 
  Dumbbell, Globe, Clock, CreditCard, MessageCircle
} from 'lucide-react';

import { LiveEventsBanner } from '@/components/LiveEventsBanner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Hero images
const HERO_IMAGES = [
  'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=1920&q=85',
  'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1920&q=85',
  'https://images.unsplash.com/photo-1582719508461-905c673771fd?w=1920&q=85'
];

// FAQ Data in Spanish
const FAQ_DATA = [
  {
    question: '¿Qué es un Certificado de Vacaciones?',
    answer: 'Los Certificados de Vacaciones son canjeables por una estadía en muchos resorts alrededor del mundo. El certificado está diseñado para uso en viajes de temporada. La disponibilidad puede variar según el destino. IMPORTANTE: Los certificados son para propósitos promocionales y no pueden ser revendidos por dinero.'
  },
  {
    question: '¿Cómo compro un Certificado?',
    answer: 'Los certificados pueden comprarse en línea y usarse como regalo o para viajes personales. Una vez que recibas el certificado, está activo inmediatamente. IMPORTANTE: El precio de compra no es por noche ni por persona.'
  },
  {
    question: '¿Los Certificados de Vacaciones expiran?',
    answer: 'El viaje debe reservarse en o antes de la "Fecha de Expiración" impresa en el certificado. El certificado está activo inmediatamente después de la compra. Tu tipo específico de certificado indicará con cuánta anticipación puedes buscar (típicamente de 30 días a 12 meses). Los certificados típicamente expiran de 180 a 365 días desde la fecha de compra. El viaje puede completarse después de que el certificado expire.'
  },
  {
    question: '¿Cómo canjeo mi Certificado y reservo mi alojamiento?',
    answer: 'Si tienes un certificado electrónico, sigue el enlace en el correo para crear una cuenta y registrar tu certificado. Si tienes un certificado impreso, visita el sitio web impreso en el certificado para crear tu cuenta. El certificado permanecerá válido hasta que sea canjeado al reservar alojamiento o expire.'
  },
  {
    question: '¿Qué alojamientos puedo obtener con mi Certificado?',
    answer: 'Todos los alojamientos son para una estadía con un día fijo de check-in y varían en tamaño desde estudios hasta 2+ habitaciones. Muchos ofrecen salas de estar, comedores, cocinas completamente equipadas e instalaciones de lavandería. Las ubicaciones de los resorts son mundiales y están basadas en disponibilidad.'
  },
  {
    question: '¿Qué es un upgrade?',
    answer: 'Por un monto adicional (típicamente entre $220 USD a $1,400 USD), puedes acceder a inventario adicional y reservar una estadía con una tarifa de canje mayor dependiendo de la propiedad y fechas seleccionadas. La tarifa de upgrade es por toda la estadía, no por noche o por persona. IMPORTANTE: Las temporadas altas pueden variar según fecha y destino.'
  },
  {
    question: '¿Puedo quedarme menos de 7 noches?',
    answer: 'Sí. Sin embargo, no recibirás un reembolso por las noches restantes de tu reserva (aplica igual para reservas con upgrade). Si planeas llegar después de la fecha de check-in programada, debes contactar al resort directamente y avisarles de tu llegada tardía. IMPORTANTE: No puedes llegar antes de la fecha de check-in.'
  },
  {
    question: '¿Puedo darle el Certificado a alguien más?',
    answer: 'Sí, puedes dar el certificado no canjeado a otra persona. IMPORTANTE: Una vez que reserves tu alojamiento, el certificado ha sido canjeado y no es transferible.'
  },
  {
    question: '¿Ya reservé mi alojamiento y ya no puedo ir. ¿Puedo cambiar mi reserva?',
    answer: 'No es posible cambiar o transferir la reserva una vez que tu alojamiento está reservado. Sin embargo, puedes agregar otro viajero a la confirmación llamando al Centro de Atención.'
  },
  {
    question: '¿Con qué frecuencia se actualiza la disponibilidad de resorts?',
    answer: 'La disponibilidad se actualiza múltiples veces al día.'
  }
];

// Testimonials
const TESTIMONIALS = [
  {
    text: 'He comprado más de 10 certificados para uso personal, regalos y recompensas para mis agentes. Son regalos perfectos para clientes y qué mejor manera de motivar a tu equipo.',
    author: 'Carlos M.',
    location: 'Ciudad de México'
  },
  {
    text: 'He usado estos por años para uso personal, dados a clientes para agradecerles por su negocio y dados a mi personal para agradecerles por su continua afiliación con nuestra firma.',
    author: 'Ana G.',
    location: 'Guadalajara, MX'
  },
  {
    text: 'Nuestra empresa ha usado los certificados por varios años. Son excelentes regalos para esos clientes especiales. El programa es simple de usar porque todo es por email/online.',
    author: 'Roberto S.',
    location: 'Monterrey, MX'
  }
];

// FAQ Item Component
function FAQItem({ question, answer, isOpen, onToggle }) {
  return (
    <div className="border-b border-slate-200">
      <button
        onClick={onToggle}
        className="w-full py-5 flex items-center justify-between text-left hover:bg-slate-50 transition-colors px-4 rounded-lg"
      >
        <span className="font-semibold text-slate-900 pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-teal-600 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <div className="px-4 pb-5">
          <p className="text-slate-600 leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [stats, setStats] = useState({ packages: 0, countries: 0 });

  useEffect(() => {
    // Rotate hero images
    const interval = setInterval(() => {
      setCurrentHeroImage(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    
    // Fetch stats
    fetchStats();
    
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const [packagesRes, countriesRes] = await Promise.all([
        fetch(`${API}/certificates?status=active`),
        fetch(`${API}/certificates/countries`)
      ]);
      const packages = await packagesRes.json();
      const countries = await countriesRes.json();
      setStats({ packages: packages.length, countries: countries.length });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <LiveEventsBanner />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2" data-testid="navbar-brand-logo">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <Plane className="w-5 h-5 text-white transform -rotate-45" />
              </div>
              <span className="font-heading text-xl font-bold text-slate-900">Bombazoo</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <a href="#como-funciona" className="text-slate-600 hover:text-teal-600 text-sm font-medium">
                ¿Cómo Funciona?
              </a>
              <a href="#preguntas" className="text-slate-600 hover:text-teal-600 text-sm font-medium">
                Preguntas Frecuentes
              </a>
              <Button
                onClick={() => navigate('/explorar-resorts')}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white"
              >
                Explorar Resorts
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/admin')}
              className="md:hidden text-slate-600"
            >
              Admin
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-16 min-h-[90vh] flex items-center">
        <div className="absolute inset-0">
          {HERO_IMAGES.map((img, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                idx === currentHeroImage ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img src={img} alt="Resort" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40" />
            </div>
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white/90 text-sm mb-6">
              <Gift className="w-4 h-4 text-amber-400" />
              <span>El Regalo Perfecto de Viaje</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-tight mb-6">
              Certificados de
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-emerald-300">
                Vacaciones en Resort
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-white/80 mb-8 leading-relaxed">
              Un regalo de viaje único, no hay mejor manera de decir "Gracias". 
              Los Certificados de Vacaciones son canjeables por estadías en destinos 
              populares y resorts afiliados alrededor del mundo.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={() => navigate('/explorar-resorts')}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-8 py-6 rounded-xl shadow-lg shadow-teal-500/30 text-lg"
              >
                <Search className="w-5 h-5 mr-2" />
                Explorar Resorts
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => {
                  document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-white/10 border-white/30 text-white hover:bg-white/20 px-8 py-6 rounded-xl text-lg"
              >
                Conocer Más
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Users, title: 'Incentiva Clientes', description: 'Recompensa la lealtad de tus clientes con experiencias inolvidables.' },
              { icon: Award, title: 'Premia Empleados', description: 'Motiva a tu equipo con el regalo de vacaciones que realmente quieren.' },
              { icon: Heart, title: 'Fortalece Relaciones', description: 'Crea vínculos duraderos con socios y colaboradores.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-all group">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is RVC Section */}
      <section id="como-funciona" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              ¿Qué es un Certificado de Vacaciones?
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Un regalo de viaje único que permite a tus seres queridos, empleados o clientes 
              disfrutar de estadías en resorts de clase mundial alrededor del mundo.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6">¿Qué obtienes?</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Un certificado canjeable por una estadía vacacional (solo alojamiento) en resorts afiliados 
                alrededor del mundo. Los condominios varían en tamaño y ofrecen alojamientos espaciosos 
                con amenidades de resort. Nuestras semanas de vacaciones provienen de la familia de 
                miembros y propietarios de tiempo compartido.
              </p>
              
              <div className="space-y-4">
                {[
                  'Estudios hasta 2+ habitaciones',
                  'Cocinas completamente equipadas',
                  'Amenidades de resort incluidas',
                  'Disponibilidad actualizada diariamente'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-teal-500" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-3xl p-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-6">¿Cómo Funciona?</h3>
              <div className="space-y-6">
                {[
                  { step: '1', title: 'Compra tu Certificado', desc: 'Elige el certificado que mejor se adapte a tus necesidades.' },
                  { step: '2', title: 'Regala o Úsalo', desc: 'Envíalo por email a alguien especial o úsalo para ti.' },
                  { step: '3', title: 'Explora Resorts', desc: 'Navega y elige entre cientos de resorts disponibles.' },
                  { step: '4', title: '¡Disfruta!', desc: 'Reserva y vive una experiencia vacacional inolvidable.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{item.title}</h4>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Spacious Accommodations */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Alojamientos Espaciosos
            </h2>
            <p className="text-lg text-slate-300">
              Variedad, Valor y Confort
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: HomeIcon,
                image: 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=600&q=80',
                title: 'Condominios de Lujo',
                description: 'Desde estudios hasta 2+ habitaciones, nuestros condominios te hacen sentir en casa incluso al otro lado del mundo.'
              },
              {
                icon: Utensils,
                image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=600&q=80',
                title: 'Cocinas Completas',
                description: 'Nuestros resorts ofrecen toda la comodidad del hogar con cocinas, salas/comedores e instalaciones de lavandería.'
              },
              {
                icon: Dumbbell,
                image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?w=600&q=80',
                title: 'Amenidades de Resort',
                description: 'Junto con alojamientos espaciosos vienen todas las actividades que esperas: piscinas, gimnasios y actividades familiares.'
              }
            ].map((item, idx) => (
              <div key={idx} className="group">
                <div className="relative h-48 rounded-2xl overflow-hidden mb-4">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-slate-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-emerald-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-white text-center">
            {[
              { value: '300,000+', label: 'Viajeros Felices' },
              { value: '78+', label: 'Destinos Exclusivos' },
              { value: '4.9/5', label: 'Calificación Promedio' },
              { value: '100%', label: 'Garantía de Satisfacción' }
            ].map((stat, idx) => (
              <div key={idx}>
                <p className="text-3xl sm:text-4xl font-bold mb-2">{stat.value}</p>
                <p className="text-teal-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Lo Que Dicen Nuestros Clientes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, idx) => (
              <div key={idx} className="bg-slate-50 rounded-2xl p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{testimonial.text}"</p>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.author}</p>
                  <p className="text-sm text-slate-500">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="preguntas" className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Preguntas Frecuentes
            </h2>
            <p className="text-lg text-slate-600">
              Todo lo que necesitas saber sobre los Certificados de Vacaciones
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            {FAQ_DATA.map((faq, idx) => (
              <FAQItem
                key={idx}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === idx}
                onToggle={() => setOpenFAQ(openFAQ === idx ? null : idx)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            ¿Listo para Explorar?
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Descubre cientos de resorts disponibles y encuentra tu próxima aventura.
          </p>
          <Button 
            size="lg"
            onClick={() => navigate('/explorar-resorts')}
            className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600 text-white px-12 py-6 rounded-xl text-lg"
          >
            Explorar Resorts
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Plane className="w-5 h-5 text-white transform -rotate-45" />
                </div>
                <span className="font-heading text-xl font-bold">Bombazoo</span>
              </div>
              <p className="text-slate-400 text-sm">
                Certificados de vacaciones premium para experiencias inolvidables.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li><a href="#como-funciona" className="hover:text-white">¿Cómo Funciona?</a></li>
                <li><a href="#preguntas" className="hover:text-white">Preguntas Frecuentes</a></li>
                <li><button onClick={() => navigate('/explorar-resorts')} className="hover:text-white">Explorar Resorts</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <ul className="space-y-2 text-slate-400 text-sm">
                <li>info@clubviajemos.com</li>
                <li>+1 954 805 9212</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Admin</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Panel de Administración
              </Button>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center text-slate-400 text-sm">
            © 2024 Bombazoo. Todos los derechos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
}
